import { Loader } from 'components';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useAuth } from 'contexts';
import dayjs from 'dayjs';
import { default as tz } from 'dayjs/plugin/timezone';
import { default as utc } from 'dayjs/plugin/utc';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getContract } from 'services/contracts';
import { Contract, Role } from 'types';
import './contract-details.scss';

dayjs.extend(utc);
dayjs.extend(tz);

const ContractDetails = () => {
  // STATE
  const navigate = useNavigate();
  const params = useParams();
  const { userProfile } = useAuth();
  const [contractId] = useState<number>(Number(params.contractId));
  const [loading, setLoading] = useState(false);
  const [contract, setContract] = useState<Contract | null>(null);

  // EFFECTS
  // Load Contract
  useEffect(() => {
    if (isNaN(contractId)) {
      return;
    }
    setLoading(true);
    getContract(contractId).then(contract => {
      setContract(contract);
      setLoading(false);
    });
  }, [contractId]);

  if (contract === null || isNaN(contractId)) {
    return (
      <>
        <p>Invalid Contract ID</p>
      </>
    );
  }
  return (
    <>
      <Loader loading={loading}>
        <div className="page contract-details-component">
          <div className="page-header">
            <h1>CONTRACT #{contract.id}</h1>
            <div className="d-flex g-3">
              <button className="btn-back" onClick={() => navigate(-1)}>
                <span className="material-icons">arrow_back_ios</span>
                BACK
              </button>
              {(userProfile.role === Role.ADMIN || userProfile.role === Role.BILLING_ADMIN) && (
                <button
                  className="btn-primary"
                  onClick={() => navigate(`/contracts/${contractId}/edit`, { replace: true })}
                >
                  EDIT
                </button>
              )}
            </div>
          </div>
          <div>
            <div className="grid-big-4">
              <GeneralInfoPanel contract={contract} />
              <EmptyContainerAndChassisCard contract={contract} />
              <EmptyBoxOnlyCard contract={contract} />
              <ChassisOnlyCard contract={contract} />
              <LoadedUnitCard contract={contract} />
            </div>
          </div>
          {/* TODO: list related invoices */}
        </div>
      </Loader>
      <SuccessDialog title="Contract Saved" />
    </>
  );
};

const GeneralInfoPanel = ({ contract }: { contract: Contract }) => {
  return (
    <div className="panel justify-content-between col-span-4">
      <div className="grid-5 p-3" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 0.5fr' }}>
        <div>
          <label>Customer</label>
          <div className="text-big">{contract.customer.name}</div>
        </div>
        <div>
          <label>Unit Size Type</label>
          <div className="text-big">{contract.unitSizeType}</div>
        </div>
        <div>
          <label>Start Date</label>
          <div className="text-big">
            {dayjs(contract.startDate)
              .tz(contract.location?.timeZone || 'UTC')
              .format('M/D/YYYY')}
          </div>
        </div>
        <div>
          <label>End Date</label>
          <div className="text-big">
            {!!contract.endDate
              ? dayjs(contract.endDate)
                  .tz(contract.location?.timeZone || 'UTC')
                  .format('M/D/YYYY')
              : 'N/A'}
          </div>
        </div>
        <div>
          <label>Active</label>
          <div className="text-big">{contract.active ? 'Yes' : 'No'}</div>
        </div>
      </div>
    </div>
  );
};

const EmptyContainerAndChassisCard = ({ contract }: { contract: Contract }) => {
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Setup</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p>{contract.freeDays}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <p>${contract.dailyEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <p>${contract.gateInEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <p>${contract.gateOutEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
      </div>
    </div>
  );
};

const EmptyBoxOnlyCard = ({ contract }: { contract: Contract }) => {
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Empty Container</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p>{contract.freeDays}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <p>${contract.dailyEmptyContainer?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <p>${contract.gateInEmptyContainer?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <p>${contract.gateOutEmptyContainer?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Lift Off</h4>
          <p>${contract.liftOffEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Lift On</h4>
          <p>${contract.liftOnEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Dig</h4>
          <p>${contract.digEmpty?.toFixed(2) ?? ' N/A'}</p>
        </div>
      </div>
    </div>
  );
};

const ChassisOnlyCard = ({ contract }: { contract: Contract }) => {
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Chassis</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p>{contract.freeDays}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <p>${contract.dailyChassis?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <p>${contract.gateInChassis?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <p>${contract.gateOutChassis?.toFixed(2) ?? ' N/A'}</p>
        </div>
      </div>
    </div>
  );
};

const LoadedUnitCard = ({ contract }: { contract: Contract }) => {
  return (
    <div className="card-primary">
      <div className="card-header">
        <h3>Loaded</h3>
      </div>
      <div className="card-body">
        <div className="grid-2">
          <h4>Free Days</h4>
          <p>{contract.freeDaysLoaded}</p>
        </div>
        <div className="grid-2">
          <h4>Daily Rate</h4>
          <p>${contract.dailyLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate In</h4>
          <p>${contract.gateInLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Gate Out</h4>
          <p>${contract.gateOutLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Lift Off</h4>
          <p>${contract.liftOffLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Lift On</h4>
          <p>${contract.liftOnLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
        <div className="grid-2">
          <h4>Dig</h4>
          <p>${contract.digLoaded?.toFixed(2) ?? ' N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default ContractDetails;
